import { Avatar, Colors, ItemList, ModalDialog, Pagination, Text } from '@amelio/ui-library';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { FilteredEmployee } from '@/hooks/filters/useRosterFilter';

import { useCompanyInformation } from '@/hooks/company/useCompanyInformation';
import { useNavigate } from '@/hooks/utils/useNavigate';
import { RouteName } from '@/navigation/types';
import { getFullName, getInitials, getProfilePictureUrl } from '@/utils/user';

const PAGE_SIZE = 10;

interface FilteredTeamModalProps {
    isOpen: boolean;
    onClose: () => void;
    employees: FilteredEmployee[];
}

/**
 * The people of the reader's team the selected filters keep, the ones the sentence under the pillar
 * scope counts, by name, a page at a time. Each opens that person's performance profile.
 */
export default function FilteredTeamModal({ isOpen, onClose, employees }: FilteredTeamModalProps) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { getJobTitle, getDepartment } = useCompanyInformation();
    const [pageNumber, setPageNumber] = useState(1);

    const sortedEmployees = useMemo(
        () => [...employees].sort((a, b) => getFullName(a).localeCompare(getFullName(b))),
        [employees],
    );
    const totalPages = Math.max(1, Math.ceil(sortedEmployees.length / PAGE_SIZE));
    // The filters can change under an open window and leave fewer pages than the one in view.
    const currentPage = Math.min(pageNumber, totalPages);
    const pageEmployees = sortedEmployees.slice(
        (currentPage - 1) * PAGE_SIZE,
        currentPage * PAGE_SIZE,
    );

    return (
        <ModalDialog
            isOpen={isOpen}
            onClose={onClose}
            closeAriaLabel={t('common.close')}
            title={t('objectives.pillarScope.filteredTeam.title')}
            subtitle={t('objectives.pillarScope.filteredTeam.subtitle', {
                count: employees.length,
            })}
            headingLevel={2}
            size={'sm'}
            backdropBlur
            dataTestId={'filtered-team-modal'}
            footer={
                totalPages > 1 ? (
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        totalCount={sortedEmployees.length}
                        pageSize={PAGE_SIZE}
                        onPageChange={setPageNumber}
                        variant={'rounded'}
                        dataTestId={'filtered-team-pagination'}
                    />
                ) : undefined
            }
        >
            <ItemList
                variant={'divided'}
                dataTestId={'filtered-team-list'}
                items={pageEmployees.map((employee) => {
                    const name = getFullName(employee);
                    const role =
                        getJobTitle(employee.jobTitleId ?? '') ||
                        getDepartment(employee.departmentId ?? '');

                    return {
                        id: employee.id,
                        content: (
                            <span className={'flex min-w-0 items-center gap-3'}>
                                <Avatar
                                    src={
                                        getProfilePictureUrl(employee.profilePictureUrl) ||
                                        undefined
                                    }
                                    alt={name}
                                    initials={getInitials(employee)}
                                    size={'2xs'}
                                    bgColor={'gradient'}
                                />

                                <span className={'flex min-w-0 flex-col gap-0.5'}>
                                    <Text
                                        as={'span'}
                                        weight={'medium'}
                                        color={Colors.BASE_600}
                                        truncate
                                    >
                                        {name}
                                    </Text>

                                    {role && (
                                        <Text
                                            as={'span'}
                                            color={Colors.BASE_500}
                                            className={'text-xs'}
                                            truncate
                                        >
                                            {role}
                                        </Text>
                                    )}
                                </span>
                            </span>
                        ),
                        onClick: () =>
                            navigate({
                                path: RouteName.PerformanceProfile.replace(':id', employee.id),
                            }),
                        ariaLabel: t('objectives.pillarScope.filteredTeam.openProfile', { name }),
                    };
                })}
            />
        </ModalDialog>
    );
}
